import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BASE_URL from '../../constants/Config';

const FULL_BASE_URL = `${BASE_URL}/api/Project`;

interface Project {
  id: number;
  title: string;
  content: string;
  photoUrl: string; // Backend'de byte array'den URL'ye dönüştürülen alan
}

interface ProjectState {
  projects: Project[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
}

const initialState: ProjectState = {
  projects: [],
  loading: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: 9,
};

// Thunks

// Projeleri sayfalar halinde çekme
export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async (pageRequest: { page: number; pageSize: number }) => {
    const response = await axios.get(`${FULL_BASE_URL}/findall`, {
      params: {
        Page: pageRequest.page,
        PageSize: pageRequest.pageSize,
      },
    });
    return response.data; // Sayfalı veri döndürülüyor
  }
);

// Proje ID'ye göre veri çekme
export const fetchProjectById = createAsyncThunk(
  'projects/fetchById',
  async (id: number) => {
    const response = await axios.get(`${FULL_BASE_URL}/find_by_id/${id}`);
    return response.data;
  }
);

// Yeni proje ekleme
export const createProject = createAsyncThunk(
  'projects/create',
  async (formData: FormData, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${FULL_BASE_URL}/save`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      throw error;
    }
  }
);

// Proje silme
export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (dto: { token: string; projectId: number }) => {
    await axios.delete(`${FULL_BASE_URL}/delete`, { data: dto });
    return dto.projectId;
  }
);

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Projeleri yüklerken
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.projects = action.payload.items || [];
          state.totalCount = action.payload.totalCount || 0;
          state.page = action.payload.page || 1;
          state.pageSize = action.payload.pageSize || 6;
        } else {
          state.projects = [];
          state.totalCount = 0;
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch projects.';
      })

      // Proje detaylarını alırken
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        } else {
          state.projects.push(action.payload);
        }
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch project by ID.';
      })

      // Yeni proje eklerken
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create project.';
      })

      // Proje silerken
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter((project) => project.id !== action.payload);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete project.';
      });
  },
});

export default projectSlice.reducer;
